// components/SalaryConfigurationSection.jsx - VERSION COMPLÈTE ENRICHIE - PARTIE 1/4
// Configuration des paramètres du système de gestion des salaires + HORAIRES & BADGES

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Settings, DollarSign, Shield, Clock, AlertTriangle, 
  Save, RotateCcw, CheckCircle, XCircle, Calculator,
  Bell, CreditCard, RefreshCw, Loader2, Info,
  Calendar, Check, User, MapPin, Building
} from 'lucide-react';

// Hook pour la configuration (déjà existant)
import { useVariableElementsConfig } from '../hooks/useVariableElements';


// ============================================================================
// LES CHAMPS, AU NIVEAU MODULE
// ============================================================================
//
// Ils etaient definis a l'interieur de SalaryConfigurationSection. Une fonction
// declaree dans un rendu change d'identite a chaque rendu : React n'y voit pas
// le meme composant, il demonte l'ancien et remonte un neuf.
//
// Consequence sur ces trente et un champs : taper un caractere appelait
// onChange, qui appelait setFormData, qui re-rendait le parent, qui recreait
// NumericField — et le champ perdait le focus. Il fallait recliquer entre
// chaque caractere.
//
// Au niveau module, leur identite est stable et React les reconcilie.
// ============================================================================

// La validation ne depend de rien : elle sort avec les champs.
const validateNumericField = (field, value, min = 0, max = null) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue) || numValue < min) return min;
  if (max !== null && numValue > max) return max;
  return numValue;
};

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <div className="flex items-start space-x-3">
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-[#246990]' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
    <div className="flex-1">
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <Badge variant={checked ? "default" : "secondary"} className="text-xs">
          {checked ? "Actif" : "Inactif"}
        </Badge>
      </div>
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
    </div>
  </div>
);

// Composant champ numérique avec validation
const NumericField = ({ 
  label, 
  value, 
  onChange, 
  min = 0, 
  max = null, 
  step = "any", 
  suffix = "", 
  description,
  required = false 
}) => (
  <div className="space-y-2">
    <Label htmlFor={label.replace(/\s+/g, '-').toLowerCase()} className="text-sm font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="relative">
      <Input
        id={label.replace(/\s+/g, '-').toLowerCase()}
        type="number"
        value={value}
        onChange={(e) => {
          const newValue = validateNumericField(label, e.target.value, min, max);
          onChange(newValue);
        }}
        min={min}
        max={max}
        step={step}
        className="pr-12"
        required={required}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
          {suffix}
        </span>
      )}
    </div>
    {description && (
      <p className="text-xs text-gray-500">{description}</p>
    )}
  </div>
);

// 🕐 NOUVEAU COMPOSANT: Champ temps
const TimeField = ({ label, value, onChange, description, required = false }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="relative">
      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
      <Input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9"
        required={required}
      />
    </div>
    {description && (
      <p className="text-xs text-gray-500">{description}</p>
    )}
  </div>
);


// ============================================================================
// 🎨 SECTIONS DE CONFIGURATION - EXISTANTES + NOUVELLES
// ============================================================================

// Section Limites Financières (EXISTANTE)

const SalaryConfigurationSection = ({ onBackToDashboard }) => {
  // ============================================================================
  // 🔧 ÉTAT ET HOOKS
  // ============================================================================
  
  // Configuration actuelle depuis l'API
  const { config, loading: configLoading, error: configError } = useVariableElementsConfig();
  
  // États locaux pour le formulaire - ENRICHI AVEC HORAIRES & BADGES
  const [formData, setFormData] = useState({
    // 💰 Limites financières
    prime_max_pourcentage: 30.00,
    retenue_max_pourcentage: 15.00,
    avance_max_pourcentage: 50.00,
    
    // ⚙️ Règles de validation
    validation_obligatoire: true,
    delai_validation_jours: 7,
    absence_impact_automatique: true,
    blocage_paiement_absences_non_validees: false,
    validation_automatique_jours: 0,
    
    // 📊 Paramètres avances
    avance_max_echeances: 6,
    avance_calcul_automatique: true,
    
    // 💡 Alertes et budget
    seuil_alerte_budget_mensuel: 1000000.00,
    alertes_budget_activees: true,
    
    // 🕐 NOUVEAUX CHAMPS HORAIRES
    horaires_ecole_debut: '07:00',
    horaires_ecole_fin: '17:00',
    pause_dejeuner_debut: '12:00',
    pause_dejeuner_fin: '14:00',
    
    // 📅 JOURS ACTIFS
    jours_actifs: {
      lundi: true,
      mardi: true,
      mercredi: true,
      jeudi: true,
      vendredi: true,
      samedi: false,
      dimanche: false
    },
    
    // 🏷️ PARAMÈTRES BADGES
    tolerance_retard_minutes: 15,
    seuil_absence_minutes: 60,
    grace_period_badgeage: 5,
    pointage_entree_obligatoire: true,
    pointage_sortie_obligatoire: true,
    calcul_heures_supplementaires: true,
    seuil_heures_sup_semaine: 40.00,
    
    // 🔔 ALERTES PRÉSENCES
    alerte_oubli_sortie: true,
    delai_alerte_oubli_heures: 2,
    notification_retards_recurrents: true,
    seuil_retards_recurrents: 3,
    periode_analyse_retards_jours: 7,
    
    // 📊 RAPPORTS AUTOMATIQUES
    rapport_mensuel_auto: true,
    jour_generation_rapport: 1,
    email_notifications_rapports: false
  });
  
  // États pour l'interface
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  // ============================================================================
  // 🔄 EFFETS ET CHARGEMENT INITIAL - ENRICHI
  // ============================================================================
  
  // Chargement de la configuration existante - AVEC NOUVEAUX CHAMPS
  useEffect(() => {
    if (config && !originalData) {
      const configData = {
        // 💰 Champs financiers existants
        prime_max_pourcentage: parseFloat(config.prime_max_pourcentage) || 30.00,
        retenue_max_pourcentage: parseFloat(config.retenue_max_pourcentage) || 15.00,
        avance_max_pourcentage: parseFloat(config.avance_max_pourcentage) || 50.00,
        validation_obligatoire: Boolean(config.validation_obligatoire),
        delai_validation_jours: parseInt(config.delai_validation_jours) || 7,
        absence_impact_automatique: Boolean(config.absence_impact_automatique),
        blocage_paiement_absences_non_validees: Boolean(config.blocage_paiement_absences_non_validees) || false,
        validation_automatique_jours: parseInt(config.validation_automatique_jours) || 0,
        avance_max_echeances: parseInt(config.avance_max_echeances) || 6,
        avance_calcul_automatique: Boolean(config.avance_calcul_automatique),
        seuil_alerte_budget_mensuel: parseFloat(config.seuil_alerte_budget_mensuel) || 1000000.00,
        alertes_budget_activees: Boolean(config.alertes_budget_activees),
        
        // 🕐 NOUVEAUX CHAMPS HORAIRES
        horaires_ecole_debut: config.horaires_ecole_debut || '07:00',
        horaires_ecole_fin: config.horaires_ecole_fin || '17:00',
        pause_dejeuner_debut: config.pause_dejeuner_debut || '12:00',
        pause_dejeuner_fin: config.pause_dejeuner_fin || '14:00',
        
        // 📅 JOURS ACTIFS
        jours_actifs: config.jours_actifs ? (
          typeof config.jours_actifs === 'string' 
            ? JSON.parse(config.jours_actifs)
            : config.jours_actifs
        ) : {
          lundi: true, mardi: true, mercredi: true, jeudi: true, vendredi: true,
          samedi: false, dimanche: false
        },
        
        // 🏷️ BADGES
        tolerance_retard_minutes: parseInt(config.tolerance_retard_minutes) || 15,
        seuil_absence_minutes: parseInt(config.seuil_absence_minutes) || 60,
        grace_period_badgeage: parseInt(config.grace_period_badgeage) || 5,
        pointage_entree_obligatoire: Boolean(config.pointage_entree_obligatoire !== false), // true par défaut
        pointage_sortie_obligatoire: Boolean(config.pointage_sortie_obligatoire !== false), // true par défaut
        calcul_heures_supplementaires: Boolean(config.calcul_heures_supplementaires !== false), // true par défaut
        seuil_heures_sup_semaine: parseFloat(config.seuil_heures_sup_semaine) || 40.00,
        
        // 🔔 ALERTES
        alerte_oubli_sortie: Boolean(config.alerte_oubli_sortie !== false), // true par défaut
        delai_alerte_oubli_heures: parseInt(config.delai_alerte_oubli_heures) || 2,
        notification_retards_recurrents: Boolean(config.notification_retards_recurrents !== false), // true par défaut
        seuil_retards_recurrents: parseInt(config.seuil_retards_recurrents) || 3,
        periode_analyse_retards_jours: parseInt(config.periode_analyse_retards_jours) || 7,
        
        // 📊 RAPPORTS
        rapport_mensuel_auto: Boolean(config.rapport_mensuel_auto !== false), // true par défaut
        jour_generation_rapport: parseInt(config.jour_generation_rapport) || 1,
        email_notifications_rapports: Boolean(config.email_notifications_rapports) || false
      };
      
      setFormData(configData);
      setOriginalData(configData);
    }
  }, [config, originalData]);

  // Détecter les changements
  useEffect(() => {
    if (originalData) {
      const changes = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(changes);
    }
  }, [formData, originalData]);

  // Auto-clear des messages de succès/erreur
  useEffect(() => {
    if (saveSuccess || saveError) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
        setSaveError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, saveError]);


  // ============================================================================
  // 🔧 GESTIONNAIRES D'ÉVÉNEMENTS - ENRICHIS
  // ============================================================================
  
  // Mise à jour des champs - AVEC GESTION SPÉCIALE POUR JOURS_ACTIFS
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Validation des valeurs numériques
  // Formatage des montants
  const formatMontant = useCallback((montant) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(montant).replace('XAF', 'FCFA');
  }, []);

  // 🕐 NOUVELLE FONCTION: Calcul durée journée de travail
  const calculateDureeJournee = useCallback((debut, fin, pauseDebut, pauseFin) => {
    try {
      const timeToMinutes = (time) => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
      };
      
      const debutMin = timeToMinutes(debut);
      const finMin = timeToMinutes(fin);
      const pauseDebutMin = timeToMinutes(pauseDebut);
      const pauseFinMin = timeToMinutes(pauseFin);
      
      const dureeTotal = finMin - debutMin;
      const dureePause = pauseFinMin - pauseDebutMin;
      const dureeTravail = dureeTotal - dureePause;
      
      const heures = Math.floor(dureeTravail / 60);
      const minutes = dureeTravail % 60;
      
      return {
        total_minutes: dureeTravail,
        heures: heures,
        minutes: minutes,
        format: `${heures}h${minutes > 0 ? minutes.toString().padStart(2, '0') : ''}`
      };
    } catch (error) {
      return { total_minutes: 0, heures: 0, minutes: 0, format: '0h' };
    }
  }, []);

  // ============================================================================
  // 💾 SAUVEGARDE ET RESET - ENRICHIS
  // ============================================================================
  
  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      setSaveError(null);
      
      const token = localStorage.getItem('token');
      
      // 🆕 PRÉPARATION SPÉCIALE POUR JSON jours_actifs
      const dataToSend = {
        ...formData,
        jours_actifs: typeof formData.jours_actifs === 'object' 
          ? JSON.stringify(formData.jours_actifs)
          : formData.jours_actifs
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://www.klasys.com'}/api/variable-elements/config/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erreur lors de la sauvegarde');
      }

      if (result.success) {
        setOriginalData({ ...formData });
        setHasChanges(false);
        setSaveSuccess(true);
      } else {
        throw new Error(result.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde configuration:', error);
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  }, [formData]);

  const handleReset = useCallback(() => {
    if (originalData) {
      setFormData({ ...originalData });
      setHasChanges(false);
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [originalData]);

  const handleResetToDefaults = useCallback(() => {
    const defaultConfig = {
      // 💰 Valeurs par défaut financières
      prime_max_pourcentage: 30.00,
      retenue_max_pourcentage: 15.00,
      avance_max_pourcentage: 50.00,
      validation_obligatoire: true,
      delai_validation_jours: 7,
      absence_impact_automatique: true,
      blocage_paiement_absences_non_validees: false,
      validation_automatique_jours: 0,
      avance_max_echeances: 6,
      avance_calcul_automatique: true,
      seuil_alerte_budget_mensuel: 1000000.00,
      alertes_budget_activees: true,
      
      // 🕐 Valeurs par défaut horaires
      horaires_ecole_debut: '07:00',
      horaires_ecole_fin: '17:00',
      pause_dejeuner_debut: '12:00',
      pause_dejeuner_fin: '14:00',
      
      // 📅 Valeurs par défaut jours
      jours_actifs: {
        lundi: true, mardi: true, mercredi: true, jeudi: true, vendredi: true,
        samedi: false, dimanche: false
      },
      
      // 🏷️ Valeurs par défaut badges
      tolerance_retard_minutes: 15,
      seuil_absence_minutes: 60,
      grace_period_badgeage: 5,
      pointage_entree_obligatoire: true,
      pointage_sortie_obligatoire: true,
      calcul_heures_supplementaires: true,
      seuil_heures_sup_semaine: 40.00,
      
      // 🔔 Valeurs par défaut alertes
      alerte_oubli_sortie: true,
      delai_alerte_oubli_heures: 2,
      notification_retards_recurrents: true,
      seuil_retards_recurrents: 3,
      periode_analyse_retards_jours: 7,
      
      // 📊 Valeurs par défaut rapports
      rapport_mensuel_auto: true,
      jour_generation_rapport: 1,
      email_notifications_rapports: false
    };
    
    setFormData(defaultConfig);
    setSaveError(null);
    setSaveSuccess(false);
  }, []);

  // ============================================================================
  // 🎨 COMPOSANTS D'INTERFACE UTILISATEUR - ENRICHIS
  // ============================================================================

  // Composant Toggle personnalisé
  const LimitesFinancieres = () => (
    <CarteReglages titre="Limites Financières" accent="#319190">
      <div className="space-y-6">
        <NumericField
          label="Prime maximale autorisée (% du salaire)"
          value={formData.prime_max_pourcentage}
          onChange={(value) => handleInputChange('prime_max_pourcentage', value)}
          min={0}
          max={100}
          suffix="%"
          description="Pourcentage maximum du salaire qu'un employé peut recevoir en prime"
          required
        />
        
        <NumericField
          label="Retenue maximale autorisée (% du salaire)"
          value={formData.retenue_max_pourcentage}
          onChange={(value) => handleInputChange('retenue_max_pourcentage', value)}
          min={0}
          max={50}
          suffix="%"
          description="Pourcentage maximum du salaire qui peut être retenu"
          required
        />
        
        <NumericField
          label="Avance maximale (% du salaire)"
          value={formData.avance_max_pourcentage}
          onChange={(value) => handleInputChange('avance_max_pourcentage', value)}
          min={0}
          max={100}
          suffix="%"
          description="Pourcentage maximum du salaire pouvant être avancé"
          required
        />

        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-800">Limites actuelles (en % du salaire)</h4>
              <ul className="text-xs text-blue-700 mt-1 space-y-1">
                <li>• Prime max : {formData.prime_max_pourcentage}% du salaire</li>
                <li>• Retenue max : {formData.retenue_max_pourcentage}% du salaire</li>
                <li>• Avance max : {formData.avance_max_pourcentage}% du salaire</li>
              </ul>
              <div className="mt-2 text-xs text-blue-600">
                <strong>Exemple pour 500 000 FCFA :</strong>
                <br />Prime max: {formatMontant(500000 * formData.prime_max_pourcentage / 100)} | 
                Retenue max: {formatMontant(500000 * formData.retenue_max_pourcentage / 100)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CarteReglages>
  );

  // Section Règles de Validation (EXISTANTE)
  const ReglesValidation = () => (
    <CarteReglages titre="Règles de Validation" accent="#246990">
      <div className="space-y-6">
        <ToggleSwitch
          checked={formData.validation_obligatoire}
          onChange={(value) => handleInputChange('validation_obligatoire', value)}
          label="Validation obligatoire"
          description="Les éléments variables doivent être validés avant application"
        />

        <NumericField
          label="Délai de validation (jours)"
          value={formData.delai_validation_jours}
          onChange={(value) => handleInputChange('delai_validation_jours', value)}
          min={1}
          max={30}
          suffix="jours"
          description="Nombre de jours pour valider un élément variable"
          required
        />

        <ToggleSwitch
          checked={formData.absence_impact_automatique}
          onChange={(value) => handleInputChange('absence_impact_automatique', value)}
          label="Impact automatique des absences"
          description="Calcul automatique de l'impact des absences sur le salaire"
        />

        <Separator />

        <ToggleSwitch
          checked={formData.blocage_paiement_absences_non_validees}
          onChange={(value) => handleInputChange('blocage_paiement_absences_non_validees', value)}
          label="Bloquer le paiement si absences non validées"
          description="Empêche le paiement tant que toutes les absences ne sont pas validées"
        />

        <NumericField
          label="Validation automatique après (jours)"
          value={formData.validation_automatique_jours}
          onChange={(value) => handleInputChange('validation_automatique_jours', value)}
          min={0}
          max={90}
          suffix="jours"
          description="0 = désactivé, sinon validation automatique après X jours"
        />

        {formData.validation_automatique_jours > 0 && (
          <div className="p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-800">
                Validation automatique activée après {formData.validation_automatique_jours} jour(s)
              </span>
            </div>
          </div>
        )}
      </div>
    </CarteReglages>
  );

  // Section Paramètres Avances (EXISTANTE)
  const ParametresAvances = () => (
    <CarteReglages titre="Paramètres des Avances" accent="#7c3aed">
      <div className="space-y-6">
        <NumericField
          label="Nombre maximum d'échéances"
          value={formData.avance_max_echeances}
          onChange={(value) => handleInputChange('avance_max_echeances', value)}
          min={1}
          max={12}
          suffix="mois"
          description="Durée maximale de remboursement d'une avance"
          required
        />

        <ToggleSwitch
          checked={formData.avance_calcul_automatique}
          onChange={(value) => handleInputChange('avance_calcul_automatique', value)}
          label="Calcul automatique des remboursements"
          description="Déduction automatique des échéances sur les salaires suivants"
        />

        <div className="p-4 bg-purple-50 rounded-lg">
          <div className="flex items-start space-x-3">
            <Calculator className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-purple-800">Exemple de calcul</h4>
              <p className="text-xs text-purple-700 mt-1">
                Avance de 300 000 FCFA sur {formData.avance_max_echeances} mois = 
                {' '}{formatMontant(300000 / formData.avance_max_echeances)} par mois
              </p>
            </div>
          </div>
        </div>
      </div>
    </CarteReglages>
  );

  // Section Alertes et Budget (EXISTANTE)
  const AlertesBudget = () => (
    <CarteReglages titre="Alertes et Budget" accent="#c0392b">
      <div className="space-y-6">
        <ToggleSwitch
          checked={formData.alertes_budget_activees}
          onChange={(value) => handleInputChange('alertes_budget_activees', value)}
          label="Alertes budget activées"
          description="Recevoir des notifications lors de dépassements de budget"
        />

        <NumericField
          label="Seuil d'alerte budget mensuel"
          value={formData.seuil_alerte_budget_mensuel}
          onChange={(value) => handleInputChange('seuil_alerte_budget_mensuel', value)}
          min={0}
          max={50000000}
          suffix="FCFA"
          description="Montant mensuel déclenchant une alerte de dépassement"
          required
        />

        {formData.alertes_budget_activees && (
          <div className="p-4 bg-red-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-red-800">Alerte configurée</h4>
                <p className="text-xs text-red-700 mt-1">
                  Une notification sera envoyée si le budget mensuel dépasse {formatMontant(formData.seuil_alerte_budget_mensuel)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </CarteReglages>
  );

  // 🕐 NOUVELLE SECTION: Horaires de l'École
  const HorairesEcole = () => (
    <CarteReglages titre="Horaires de l'École" accent="#246990">
      <div className="space-y-6">
        {/* Horaires principaux */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TimeField
            label="Heure d'ouverture"
            value={formData.horaires_ecole_debut}
            onChange={(value) => handleInputChange('horaires_ecole_debut', value)}
            description="Heure d'ouverture de l'école"
            required
          />
          
          <TimeField
            label="Heure de fermeture"
            value={formData.horaires_ecole_fin}
            onChange={(value) => handleInputChange('horaires_ecole_fin', value)}
            description="Heure de fermeture de l'école"
            required
          />
        </div>
        
        {/* Pause déjeuner */}
        <Separator />
        <div>
          <h4 className="text-sm font-medium text-gray-800 mb-3">Pause déjeuner</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TimeField
              label="Début pause"
              value={formData.pause_dejeuner_debut}
              onChange={(value) => handleInputChange('pause_dejeuner_debut', value)}
              description="Début de la pause déjeuner"
            />
            
            <TimeField
              label="Fin pause"
              value={formData.pause_dejeuner_fin}
              onChange={(value) => handleInputChange('pause_dejeuner_fin', value)}
              description="Fin de la pause déjeuner"
            />
          </div>
        </div>
        
        {/* Aperçu durée journée */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-800">Aperçu de la journée</h4>
              <div className="text-xs text-blue-700 mt-1">
                {(() => {
                  const dureeInfo = calculateDureeJournee(
                    formData.horaires_ecole_debut,
                    formData.horaires_ecole_fin,
                    formData.pause_dejeuner_debut,
                    formData.pause_dejeuner_fin
                  );
                  
                  const timeToMinutes = (time) => {
                    const [h, m] = time.split(':').map(Number);
                    return h * 60 + m;
                  };
                  
                  const debut = timeToMinutes(formData.horaires_ecole_debut);
                  const fin = timeToMinutes(formData.horaires_ecole_fin);
                  const pauseDebut = timeToMinutes(formData.pause_dejeuner_debut);
                  const pauseFin = timeToMinutes(formData.pause_dejeuner_fin);
                  
                  const dureeTotal = fin - debut;
                  const dureePause = pauseFin - pauseDebut;
                  
                  return (
                    <div className="space-y-1">
                      <div>Durée totale: {Math.floor(dureeTotal / 60)}h{(dureeTotal % 60).toString().padStart(2, '0')}</div>
                      <div>Pause: {Math.floor(dureePause / 60)}h{(dureePause % 60).toString().padStart(2, '0')}</div>
                      <div><strong>Temps de travail: {dureeInfo.format}</strong></div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CarteReglages>
  );

  // 📅 NOUVELLE SECTION: Jours de Travail
  const JoursTravail = () => {
    const jours = [
      { key: 'lundi', label: 'Lundi', short: 'Lun' },
      { key: 'mardi', label: 'Mardi', short: 'Mar' },
      { key: 'mercredi', label: 'Mercredi', short: 'Mer' },
      { key: 'jeudi', label: 'Jeudi', short: 'Jeu' },
      { key: 'vendredi', label: 'Vendredi', short: 'Ven' },
      { key: 'samedi', label: 'Samedi', short: 'Sam' },
      { key: 'dimanche', label: 'Dimanche', short: 'Dim' }
    ];

    return (
      <CarteReglages titre="Jours de Travail" accent="#319190">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
            {jours.map((jour) => (
              <button
                key={jour.key}
                type="button"
                onClick={() => handleInputChange('jours_actifs', {
                  ...formData.jours_actifs,
                  [jour.key]: !formData.jours_actifs[jour.key]
                })}
                className={`
                  flex flex-col items-center p-3 rounded-lg border-2 transition-all
                  ${formData.jours_actifs[jour.key]
                    ? 'bg-[#246990] border-[#246990] text-white shadow-md'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-[#246990] hover:bg-[#246990]/5'
                  }
                `}
              >
                <span className="text-sm font-medium">{jour.short}</span>
                <span className="text-xs opacity-75">{jour.label}</span>
                {formData.jours_actifs[jour.key] && (
                  <Check className="mt-1" size={14} />
                )}
              </button>
            ))}
          </div>
          
          {/* Résumé jours actifs */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Jours actifs:</span>
              <span className="font-medium">
                {Object.values(formData.jours_actifs).filter(Boolean).length}/7 jours
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {Object.entries(formData.jours_actifs)
                .filter(([jour, actif]) => actif)
                .map(([jour]) => jour.charAt(0).toUpperCase() + jour.slice(1))
                .join(', ')}
            </div>
          </div>

          {/* Avertissement si aucun jour actif */}
          {Object.values(formData.jours_actifs).filter(Boolean).length === 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-800">Au moins un jour doit être actif</span>
              </div>
            </div>
          )}
        </div>
      </CarteReglages>
    );
  };

  // 🏷️ NOUVELLE SECTION: Paramètres Badges
  const ParametresBadges = () => (
    <CarteReglages titre="Paramètres Badges" accent="#c66d59">
      <div className="space-y-6">
        {/* Tolérances */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumericField
            label="Tolérance retard (minutes)"
            value={formData.tolerance_retard_minutes}
            onChange={(value) => handleInputChange('tolerance_retard_minutes', value)}
            min={0}
            max={120}
            suffix="min"
            description="Retard autorisé sans impact"
            required
          />
          
          <NumericField
            label="Seuil absence (minutes)"
            value={formData.seuil_absence_minutes}
            onChange={(value) => handleInputChange('seuil_absence_minutes', value)}
            min={1}
            max={480}
            suffix="min"
            description="Au-delà: considéré comme absence"
            required
          />
        </div>
        
        {/* Règles pointage */}
        <Separator />
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-800">Règles de pointage</h4>
          
          <ToggleSwitch
            checked={formData.pointage_entree_obligatoire}
            onChange={(value) => handleInputChange('pointage_entree_obligatoire', value)}
            label="Pointage d'entrée obligatoire"
            description="L'employé doit obligatoirement badger à l'entrée"
          />
          
          <ToggleSwitch
            checked={formData.pointage_sortie_obligatoire}
            onChange={(value) => handleInputChange('pointage_sortie_obligatoire', value)}
            label="Pointage de sortie obligatoire"
            description="L'employé doit obligatoirement badger à la sortie"
          />
          
          <NumericField
            label="Période de grâce (minutes)"
            value={formData.grace_period_badgeage}
            onChange={(value) => handleInputChange('grace_period_badgeage', value)}
            min={1}
            max={60}
            suffix="min"
            description="Délai minimum entre deux badgeages"
          />
        </div>
        
        {/* Heures supplémentaires */}
        <Separator />
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-800">Heures supplémentaires</h4>
          
          <ToggleSwitch
            checked={formData.calcul_heures_supplementaires}
            onChange={(value) => handleInputChange('calcul_heures_supplementaires', value)}
            label="Calcul automatique des heures sup"
            description="Calcul automatique basé sur les horaires et présences"
          />
          
          {formData.calcul_heures_supplementaires && (
            <NumericField
              label="Seuil heures sup (par semaine)"
              value={formData.seuil_heures_sup_semaine}
              onChange={(value) => handleInputChange('seuil_heures_sup_semaine', value)}
              min={1}
              max={80}
              step="0.5"
              suffix="h"
              description="Nombre d'heures au-delà duquel les heures sup sont comptées"
              required
            />
          )}
        </div>
        
        {/* Aperçu configuration */}
        <div className="p-4 bg-orange-50 rounded-lg">
          <div className="flex items-start space-x-3">
            <Settings className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-orange-800">Configuration active</h4>
              <div className="text-xs text-orange-700 mt-1 space-y-1">
                <div>Retard toléré: {formData.tolerance_retard_minutes} min</div>
                <div>Seuil absence: {formData.seuil_absence_minutes} min</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span>Pointage :</span>
                  {formData.pointage_entree_obligatoire
                    ? <Check size={13} className="text-orange-700" />
                    : <XCircle size={13} className="text-orange-400" />}
                  <span>Entrée,</span>
                  {formData.pointage_sortie_obligatoire
                    ? <Check size={13} className="text-orange-700" />
                    : <XCircle size={13} className="text-orange-400" />}
                  <span>Sortie</span>
                </div>
                {formData.calcul_heures_supplementaires && (
                  <div>Heures sup après: {formData.seuil_heures_sup_semaine}h/semaine</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CarteReglages>
  );

  // 🔔 NOUVELLE SECTION: Alertes et Notifications
  const AlertesNotifications = () => (
    <CarteReglages titre="Alertes et Notifications" accent="#c0392b">
      <div className="space-y-6">
        {/* Alertes oubli */}
        <ToggleSwitch
          checked={formData.alerte_oubli_sortie}
          onChange={(value) => handleInputChange('alerte_oubli_sortie', value)}
          label="Alerte oubli de sortie"
          description="Notification si l'employé oublie de badger à la sortie"
        />
        
        {formData.alerte_oubli_sortie && (
          <NumericField
            label="Délai avant alerte (heures)"
            value={formData.delai_alerte_oubli_heures}
            onChange={(value) => handleInputChange('delai_alerte_oubli_heures', value)}
            min={1}
            max={24}
            suffix="h"
            description="Temps d'attente avant de déclencher l'alerte"
          />
        )}
        
        {/* Retards récurrents */}
        <Separator />
        <ToggleSwitch
          checked={formData.notification_retards_recurrents}
          onChange={(value) => handleInputChange('notification_retards_recurrents', value)}
          label="Notification retards récurrents"
          description="Alerte en cas de retards répétés sur une période"
        />
        
        {formData.notification_retards_recurrents && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumericField
              label="Seuil de retards"
              value={formData.seuil_retards_recurrents}
              onChange={(value) => handleInputChange('seuil_retards_recurrents', value)}
              min={1}
              max={20}
              suffix="retards"
              description="Nombre de retards pour déclencher l'alerte"
            />
            
            <NumericField
              label="Période d'analyse (jours)"
              value={formData.periode_analyse_retards_jours}
              onChange={(value) => handleInputChange('periode_analyse_retards_jours', value)}
              min={1}
              max={30}
              suffix="jours"
              description="Période sur laquelle analyser les retards"
            />
          </div>
        )}
        
        {/* Rapports automatiques */}
        <Separator />
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-800">Rapports automatiques</h4>
          
          <ToggleSwitch
            checked={formData.rapport_mensuel_auto}
            onChange={(value) => handleInputChange('rapport_mensuel_auto', value)}
            label="Génération automatique des rapports mensuels"
            description="Création automatique des rapports de présence chaque mois"
          />
          
          {formData.rapport_mensuel_auto && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumericField
                label="Jour de génération"
                value={formData.jour_generation_rapport}
                onChange={(value) => handleInputChange('jour_generation_rapport', value)}
                min={1}
                max={28}
                suffix="du mois"
                description="Jour du mois pour génération automatique"
              />
              
              <div className="flex items-center">
                <ToggleSwitch
                  checked={formData.email_notifications_rapports}
                  onChange={(value) => handleInputChange('email_notifications_rapports', value)}
                  label="Envoi par email"
                  description="Envoyer automatiquement les rapports par email"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </CarteReglages>
  );

// ============================================================================
  // 🎮 BARRE D'ACTIONS ET ÉTAT - ENRICHIE
  // ============================================================================

  // Barre d'actions avec sauvegarde et reset
  const ActionBar = () => (
    <Card className="sticky top-4 z-10 border-l-4 border-l-[#246990]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-[#246990]" />
              <h3 className="text-lg font-semibold text-gray-800">Configuration Complète</h3>
            </div>
            
            {hasChanges && (
              <Badge variant="outline" className="text-orange-600 border-orange-600">
                <Clock className="w-3 h-3 mr-1" />
                Modifications non sauvegardées
              </Badge>
            )}
            
            {saveSuccess && (
              <Badge variant="default" className="bg-green-600">
                <CheckCircle className="w-3 h-3 mr-1" />
                Sauvegardé avec succès
              </Badge>
            )}
            
            {saveError && (
              <Badge variant="destructive">
                <XCircle className="w-3 h-3 mr-1" />
                Erreur de sauvegarde
              </Badge>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetToDefaults}
              disabled={saving}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Valeurs par défaut
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={!hasChanges || saving}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-[#246990] hover:bg-[#1e5a7a] text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </div>

        {saveError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Erreur de sauvegarde</p>
                <p className="text-xs text-red-600 mt-1">{saveError}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // ============================================================================
  // 🎨 COMPOSANT PRINCIPAL DE RENDU - ENRICHI
  // ============================================================================

  // États de chargement et d'erreur
  if (configLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          <ChargementSection accent="#246990" libelle="Chargement de la configuration…" />
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="space-y-6">
        <Card className="border-red-200">
          <CardContent className="p-8">
            <div className="text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-red-800 mb-2">
                Erreur de chargement
              </h3>
              <p className="text-red-600 mb-4">{configError}</p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Réessayer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // 🎯 RENDU PRINCIPAL ENRICHI
  // ============================================================================

  return (
    <div className="space-y-6 pb-8">
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={Calculator}
        titre="Paramétrage de la gestion comptable"
        sousTitre="Limites financières, règles de validation, alertes budgétaires et horaires"
      />

      {/* Les sections sont APPELEES, pas montees comme composants.
          `<LimitesFinancieres />` creait un type neuf a chaque rendu : React
          demontait tout le sous-arbre, y compris les champs de saisie. Les
          appeler inline supprime la frontiere de composant, et le focus tient
          d'un caractere au suivant. Aucune n'utilise de hook, l'appel est donc
          equivalent. */}
      {ActionBar()}

      <Tabs defaultValue="limites" className="w-full">
        <TabsList className="grid grid-cols-4 mb-6 bg-[#246990]/8 p-1 rounded-xl">
          <TabsTrigger
            value="limites"
            className="data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <DollarSign size={15} className="mr-2" />
            <span>Limites</span>
          </TabsTrigger>
          <TabsTrigger
            value="regles"
            className="data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <Shield size={15} className="mr-2" />
            <span>Règles</span>
          </TabsTrigger>
          <TabsTrigger
            value="horaires"
            className="data-[state=active]:bg-[#ae7467] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <Clock size={15} className="mr-2" />
            <span>Horaires et badges</span>
          </TabsTrigger>
          <TabsTrigger
            value="alertes"
            className="data-[state=active]:bg-[#7c3aed] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <Bell size={15} className="mr-2" />
            <span>Alertes</span>
          </TabsTrigger>
        </TabsList>

        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <TabsContent value="limites" className="mt-0 focus-visible:outline-none">
            <div className="space-y-6">
              {LimitesFinancieres()}
              {ParametresAvances()}
            </div>
          </TabsContent>

          <TabsContent value="regles" className="mt-0 focus-visible:outline-none">
            <div className="space-y-6">
              {ReglesValidation()}
              {JoursTravail()}
            </div>
          </TabsContent>

          <TabsContent value="horaires" className="mt-0 focus-visible:outline-none">
            <div className="space-y-6">
              {HorairesEcole()}
              {ParametresBadges()}
            </div>
          </TabsContent>

          <TabsContent value="alertes" className="mt-0 focus-visible:outline-none">
            <div className="space-y-6">
              {AlertesBudget()}
              {AlertesNotifications()}
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Résumé de la configuration - ENRICHI */}
      <CarteReglages titre="Résumé de la configuration" accent="#246990">
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Cartes existantes */}
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Prime max</p>
              <p className="text-lg font-bold text-green-600">
                {formData.prime_max_pourcentage}%
              </p>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <CreditCard className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Retenue max</p>
              <p className="text-lg font-bold text-red-600">
                {formData.retenue_max_pourcentage}%
              </p>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <CreditCard className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Avance max</p>
              <p className="text-lg font-bold text-purple-600">
                {formData.avance_max_pourcentage}%
              </p>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <Bell className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Seuil budget</p>
              <p className="text-lg font-bold text-orange-600">
                {formatMontant(formData.seuil_alerte_budget_mensuel)}
              </p>
            </div>
            
            {/* 🆕 NOUVELLES CARTES */}
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Horaires école</p>
              <p className="text-lg font-bold text-blue-600">
                {formData.horaires_ecole_debut} - {formData.horaires_ecole_fin}
              </p>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Jours actifs</p>
              <p className="text-lg font-bold text-green-600">
                {Object.values(formData.jours_actifs).filter(Boolean).length}/7
              </p>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <CreditCard className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Tolérance retard</p>
              <p className="text-lg font-bold text-orange-600">
                {formData.tolerance_retard_minutes} min
              </p>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <Bell className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Alertes actives</p>
              <p className="text-lg font-bold text-red-600">
                {[
                  formData.alerte_oubli_sortie,
                  formData.notification_retards_recurrents,
                  formData.rapport_mensuel_auto
                ].filter(Boolean).length}/3
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Indicateurs existants */}
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.validation_obligatoire ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Validation obligatoire</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.absence_impact_automatique ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Impact absences auto</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.blocage_paiement_absences_non_validees ? 'bg-orange-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Blocage si absences</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.alertes_budget_activees ? 'bg-red-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Alertes budget</span>
            </div>
            
            {/* 🆕 NOUVEAUX INDICATEURS */}
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.pointage_entree_obligatoire ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Pointage entrée</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.pointage_sortie_obligatoire ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Pointage sortie</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.calcul_heures_supplementaires ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Heures sup auto</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.email_notifications_rapports ? 'bg-purple-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600">Email rapports</span>
            </div>
          </div>
        </div>
      </CarteReglages>

      {/* Note d'information - ENRICHIE */}
      {/* Encadre d'information — norme section 8 : teinte de la couleur
          principale, pastille d'icone blanche, une question puis sa reponse. */}
      <div className="rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
        <div className="flex gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
            <Info className="h-4 w-4 text-[#246990]" />
          </div>
            <div className="text-xs leading-relaxed text-gray-600">
              <p className="mb-1 text-[13px] font-semibold text-[#246990]">Ce que règle cette page</p>
              <p className="text-xs text-gray-600 mt-1">
                Cette configuration centrale gère les limites financières, horaires de l'école, 
                paramètres badges, alertes et rapports automatiques. Les modifications s'appliquent 
                immédiatement à tous les nouveaux éléments et processus du système.
              </p>
              <div className="mt-2 text-xs text-blue-600">
                <strong>Sections configurées :</strong> Salaires et primes • Horaires école • 
                Jours de travail • Système badges • Alertes présences • Rapports automatiques
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default SalaryConfigurationSection;
